import { API } from "./api";

/**
 * SSE consumer built on fetch + ReadableStream so we can send Authorization
 * headers (native EventSource can't). Kept flat via helper functions so the
 * main entrypoint stays readable and testable.
 */

function readAuthToken() {
  return localStorage.getItem("contentos_token");
}

async function extractHttpErrorMessage(resp) {
  const raw = await resp.text().catch(() => "");
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.detail === "string") return parsed.detail;
  } catch { /* not JSON, fall through */ }
  return raw || `HTTP ${resp.status}`;
}

function parseFrame(frame) {
  let event = "message";
  let data = "";
  for (const line of frame.split("\n")) {
    if (line.startsWith("event: ")) event = line.slice(7).trim();
    else if (line.startsWith("data: ")) data += line.slice(6);
  }
  if (!data) return null;
  let payload;
  try { payload = JSON.parse(data); } catch { payload = { text: data }; }
  return { event, payload };
}

function dispatch(event, payload, handlers) {
  const routes = {
    start: handlers.onStart,
    token: handlers.onToken,
    done: handlers.onDone,
    error: (p) => handlers.onError && handlers.onError(new Error(p.error || "Stream error")),
  };
  const fn = routes[event];
  if (fn) fn(payload);
}

async function pumpStream(resp, handlers) {
  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const frames = buffer.split("\n\n");
    buffer = frames.pop() || "";
    for (const frame of frames) {
      const parsed = parseFrame(frame);
      if (parsed) dispatch(parsed.event, parsed.payload, handlers);
    }
  }
}

/**
 * @param {string} path  e.g. "/api/performance/review/stream"
 * @param {object} handlers { onStart, onToken, onDone, onError }
 * @returns {() => void} abort function
 */
export function openSSE(path, handlers = {}) {
  const controller = new AbortController();
  const token = readAuthToken();

  (async () => {
    try {
      const resp = await fetch(`${API.replace(/\/api$/, "")}${path}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream",
        },
        signal: controller.signal,
      });
      if (!resp.ok || !resp.body) {
        throw new Error(await extractHttpErrorMessage(resp));
      }
      await pumpStream(resp, handlers);
      if (handlers.onDone) handlers.onDone({});
    } catch (e) {
      if (e.name !== "AbortError" && handlers.onError) handlers.onError(e);
    }
  })();

  return () => controller.abort();
}
